import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, User, Menu, X, Settings, ChevronDown } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import MadBrothersLogo from './MadBrothersLogo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin email - users with this email see the admin link
const ADMIN_EMAIL = "madbrothersdublin@gmail.com";

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const { itemCount } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  // Fetch categories from backend
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Static navigation links
  const staticLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-stone-100" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex-shrink-0">
            <MadBrothersLogo size="xl" variant="dark" />
          </Link>

          {/* Desktop Navigation - Elegant Cormorant Garamond font - thicker */}
          <div className="hidden lg:flex items-center gap-7">
            {/* Home link */}
            <Link
              to="/"
              className="font-['Cormorant_Garamond'] text-[#2d2d2d] hover:text-[#8D6E63] font-bold transition-all duration-300 relative group text-[18px]"
              style={{ WebkitTextStroke: '0.5px #2d2d2d' }}
              data-testid="nav-home"
            >
              Home
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-0 h-[1.5px] bg-[#8D6E63] group-hover:w-full transition-all duration-300" />
            </Link>

            {/* Products Dropdown with all categories */}
            <DropdownMenu>
              <DropdownMenuTrigger 
                className="font-['Cormorant_Garamond'] text-[#2d2d2d] hover:text-[#8D6E63] font-bold transition-all duration-300 text-[18px] flex items-center gap-1 outline-none"
                style={{ WebkitTextStroke: '0.5px #2d2d2d' }}
              >
                Products
                <ChevronDown className="w-4 h-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 font-['Cormorant_Garamond'] bg-white/95 backdrop-blur-md border-stone-100">
                <DropdownMenuItem onClick={() => navigate('/shop')} data-testid="nav-all-products" className="text-[16px] font-semibold">
                  All Products
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-stone-100" />
                {categories.map((category) => (
                  <DropdownMenuItem 
                    key={category.id} 
                    onClick={() => navigate(`/shop?category=${encodeURIComponent(category.id)}`)}
                    data-testid={`nav-category-${category.id}`}
                    className="text-[16px] font-semibold"
                  >
                    {category.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* About Us link - nowrap to keep in one line */}
            <Link
              to="/about"
              className="font-['Cormorant_Garamond'] text-[#2d2d2d] hover:text-[#8D6E63] font-bold transition-all duration-300 relative group text-[18px] whitespace-nowrap"
              style={{ WebkitTextStroke: '0.5px #2d2d2d' }}
              data-testid="nav-about"
            >
              About Us
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-0 h-[1.5px] bg-[#8D6E63] group-hover:w-full transition-all duration-300" />
            </Link>

            {/* Contact link */}
            <Link
              to="/contact"
              className="font-['Cormorant_Garamond'] text-[#2d2d2d] hover:text-[#8D6E63] font-bold transition-all duration-300 relative group text-[18px] whitespace-nowrap"
              style={{ WebkitTextStroke: '0.5px #2d2d2d' }}
              data-testid="nav-contact"
            >
              Contact
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-0 h-[1.5px] bg-[#8D6E63] group-hover:w-full transition-all duration-300" />
            </Link>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Cart */}
            <Link to="/cart" className="relative p-2.5 hover:bg-stone-50 rounded-full transition-all duration-200" data-testid="cart-link">
              <ShoppingBag className="w-5 h-5 text-[#3d3d3d]" strokeWidth={1.5} />
              {itemCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#8D6E63] text-white text-[10px] rounded-full flex items-center justify-center font-medium"
                  data-testid="cart-count"
                >
                  {itemCount}
                </motion.span>
              )}
            </Link>

            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full hover:bg-stone-50" data-testid="user-menu-trigger">
                    <User className="w-5 h-5 text-[#3d3d3d]" strokeWidth={1.5} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 font-['Cormorant_Garamond'] bg-white/95 backdrop-blur-md border-stone-100">
                  <div className="px-3 py-2">
                    <p className="text-sm font-semibold text-[#3d3d3d]">{user?.name}</p>
                    <p className="text-xs text-stone-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-stone-100" />
                  <DropdownMenuItem onClick={() => navigate('/account')} data-testid="account-link" className="text-[15px]">
                    My Account
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/account/orders')} data-testid="orders-link" className="text-[15px]">
                    Order History
                  </DropdownMenuItem>
                  {user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase() && (
                    <>
                      <DropdownMenuSeparator className="bg-stone-100" />
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin')} 
                        data-testid="admin-link"
                        className="text-[#8D6E63] font-medium text-[15px]"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Painel Admin
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-stone-100" />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-[15px]">
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/login')}
                className="hidden sm:flex rounded-full border-[#3d3d3d] text-[#3d3d3d] hover:bg-[#3d3d3d] hover:text-white font-['Cormorant_Garamond'] font-medium text-[14px] px-5 h-9 transition-all duration-300"
                data-testid="login-btn"
              >
                Login
              </Button>
            )}

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 hover:bg-stone-100 rounded-full"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-stone-200"
          >
            <div className="px-4 py-4 space-y-3">
              {/* Home */}
              <Link
                to="/"
                className="block py-2 text-[#1a1a1a] font-['Poppins'] font-medium hover:text-[#8D6E63]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>

              {/* All Products */}
              <Link
                to="/shop"
                className="block py-2 text-[#1a1a1a] font-['Poppins'] font-medium hover:text-[#8D6E63]"
                onClick={() => setMobileMenuOpen(false)}
              >
                All Products
              </Link>

              {/* Dynamic Categories */}
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/shop?category=${encodeURIComponent(category.id)}`}
                  className="block py-2 pl-4 text-[#1a1a1a] font-['Poppins'] font-medium hover:text-[#8D6E63] border-l-2 border-[#8D6E63]/30"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {category.name}
                </Link>
              ))}

              {/* About Us */}
              <Link
                to="/about"
                className="block py-2 text-[#1a1a1a] font-['Poppins'] font-medium hover:text-[#8D6E63]"
                onClick={() => setMobileMenuOpen(false)}
              >
                About Us
              </Link>

              {/* Contact */}
              <Link
                to="/contact"
                className="block py-2 text-[#1a1a1a] font-['Poppins'] font-medium hover:text-[#8D6E63]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact
              </Link>

              {!isAuthenticated && (
                <Button
                  className="w-full mt-4 bg-[#1a1a1a] hover:bg-[#1a1a1a]/90 rounded-full font-['Poppins']"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/login');
                  }}
                >
                  Login
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
